import { useCallback, useEffect, useRef, useState } from 'react';

export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 420;
export const SIDEBAR_DEFAULT_WIDTH = 256;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

const STORAGE_KEY = 'labelify_sidebar';
const KEYBOARD_STEP = 16;
// drag this far past the minimum and the sidebar snaps shut rather than fighting the clamp
const COLLAPSE_SNAP = 40;

const clampWidth = (px) =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(px)));

function readStored() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!stored) return null;
    return {
      width: clampWidth(Number(stored.width) || SIDEBAR_DEFAULT_WIDTH),
      collapsed: Boolean(stored.collapsed),
    };
  } catch {
    return null;
  }
}

/**
 * Drag-to-resize state for the dashboard sidebar.
 *
 * Nineteen nav labels do not all fit the same width — "External Processing"
 * needs room the shorter roles waste — so the width is the operator's call and
 * survives reloads. Collapse rides along in the same record: it is the same
 * decision at its far end, and losing it on every reload was the old annoyance.
 *
 * Dragging below the minimum collapses instead of sticking at 200px, which
 * makes the handle a complete control — the toggle button is a shortcut, not
 * the only way in or out.
 */
export function useSidebarWidth() {
  const [state, setState] = useState(
    () => readStored() || { width: SIDEBAR_DEFAULT_WIDTH, collapsed: false }
  );
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  // the pointer leaves the 6px handle long before the drag ends, so the cursor
  // and selection lock live on the body for the duration
  useEffect(() => {
    if (!resizing) return undefined;
    const { body } = document;
    const previous = { cursor: body.style.cursor, userSelect: body.style.userSelect };
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';
    return () => {
      body.style.cursor = previous.cursor;
      body.style.userSelect = previous.userSelect;
    };
  }, [resizing]);

  const applyWidth = useCallback((px) => {
    setState((current) =>
      px < SIDEBAR_MIN_WIDTH - COLLAPSE_SNAP
        ? { ...current, collapsed: true }
        : { width: clampWidth(px), collapsed: false }
    );
  }, []);

  const toggleCollapsed = useCallback(() => {
    setState((current) => ({ ...current, collapsed: !current.collapsed }));
  }, []);

  const resetWidth = useCallback(() => {
    setState({ width: SIDEBAR_DEFAULT_WIDTH, collapsed: false });
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current = {
        startX: event.clientX,
        startWidth: state.collapsed ? SIDEBAR_COLLAPSED_WIDTH : state.width,
      };
      setResizing(true);
    },
    [state.collapsed, state.width]
  );

  const onPointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      applyWidth(drag.startWidth + (event.clientX - drag.startX));
    },
    [applyWidth]
  );

  const endDrag = useCallback((event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setResizing(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case 'ArrowLeft':
          // stepping down from the minimum is how the keyboard reaches collapsed
          if (!state.collapsed) {
            if (state.width <= SIDEBAR_MIN_WIDTH) toggleCollapsed();
            else applyWidth(state.width - KEYBOARD_STEP);
          }
          break;
        case 'ArrowRight':
          if (state.collapsed) {
            setState((current) => ({ ...current, collapsed: false }));
          } else {
            applyWidth(state.width + KEYBOARD_STEP);
          }
          break;
        case 'Home':
          setState({ width: SIDEBAR_MIN_WIDTH, collapsed: false });
          break;
        case 'End':
          setState({ width: SIDEBAR_MAX_WIDTH, collapsed: false });
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          toggleCollapsed();
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    [state.collapsed, state.width, applyWidth, toggleCollapsed]
  );

  return {
    width: state.collapsed ? SIDEBAR_COLLAPSED_WIDTH : state.width,
    collapsed: state.collapsed,
    resizing,
    toggleCollapsed,
    resizeHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onLostPointerCapture: endDrag,
      onKeyDown,
      onDoubleClick: resetWidth,
    },
  };
}

export default useSidebarWidth;
